import { useWaitingParticipants } from '@daily-co/daily-react';
import { useSubjectLanguageVariants } from '@features/user';
import { Check as CheckIcon, ContentCopy as CopyIcon, Send as SendIcon } from '@mui/icons-material';
import { Button, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useCallback, useEffect, useRef } from 'react';

interface WaitingParticipant {
  id: string;
  name: string;
}

interface WaitingParticipantsProps {
  isOwner: boolean;
  appointmentId?: string;
  hasRemoteParticipants?: boolean;
  onCopyLink?: () => void;
  isCopyingLink?: boolean;
  linkCopied?: boolean;
  onSendToPatient?: () => void;
}

export function WaitingParticipants({
  isOwner,
  appointmentId,
  hasRemoteParticipants,
  onCopyLink,
  isCopyingLink,
  linkCopied,
  onSendToPatient,
}: WaitingParticipantsProps) {
  const { singularCap } = useSubjectLanguageVariants();
  const { waitingParticipants, grantAccess, denyAccess } = useWaitingParticipants();
  const { telehealth } = useEventTracking();
  const participants = waitingParticipants as WaitingParticipant[];

  const handleAdmit = useCallback(
    (participantId: string) => {
      telehealth.participantAdmitted({
        appointment_id: appointmentId,
      });
      grantAccess(participantId);
    },
    [telehealth, appointmentId, grantAccess],
  );

  const handleDeny = useCallback(
    (participantId: string) => {
      telehealth.participantDenied({
        appointment_id: appointmentId,
      });
      denyAccess(participantId);
    },
    [telehealth, appointmentId, denyAccess],
  );

  // Track when waiting room requests are shown to the clinician.
  // Uses a Set to fire only once per unique participant.
  const trackedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!isOwner || participants.length === 0) return;
    const newIds = participants.filter(p => !trackedIdsRef.current.has(p.id));
    if (newIds.length === 0) return;
    newIds.forEach(p => trackedIdsRef.current.add(p.id));
    telehealth.waitingRoomRequestShown({
      appointment_id: appointmentId,
      participant_count: participants.length,
    });
  }, [isOwner, participants, appointmentId, telehealth]);

  if (!isOwner) return null;

  // Participants waiting — compact admit/deny row
  if (participants.length > 0) {
    return (
      <Collapse in>
        <Stack spacing={0.5}>
          {participants.map(p => (
            <Stack
              key={p.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ bgcolor: 'grey.800', p: 1, borderRadius: 2 }}
            >
              <Typography variant="body2" sx={{ color: 'common.white', flex: 1 }}>
                <strong>{p.name || 'Guest'}</strong> wants to join the call
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => handleAdmit(p.id)}
                aria-label={`Admit ${p.name || 'guest'}`}
                sx={{ textTransform: 'none' }}
              >
                Admit
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleDeny(p.id)}
                aria-label={`Deny ${p.name || 'guest'}`}
                sx={{
                  color: 'grey.400',
                  borderColor: 'rgba(255,255,255,0.3)',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.5)',
                    bgcolor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                Deny
              </Button>
            </Stack>
          ))}
        </Stack>
      </Collapse>
    );
  }

  // No participants waiting — compact invite buttons if provider is alone
  if (!onCopyLink || hasRemoteParticipants) return null;

  return (
    <Stack direction="row" spacing={1} sx={{ bgcolor: 'grey.800', p: 1, borderRadius: 2 }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={
          isCopyingLink ? (
            <CircularProgress size={14} />
          ) : linkCopied ? (
            <CheckIcon sx={{ fontSize: 16 }} />
          ) : (
            <CopyIcon sx={{ fontSize: 16 }} />
          )
        }
        onClick={onCopyLink}
        disabled={isCopyingLink || linkCopied}
        sx={{
          color: 'common.white',
          borderColor: 'rgba(255,255,255,0.3)',
          textTransform: 'none',
          '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.5)',
            borderColor: 'rgba(255, 255, 255, 0.2)',
          },
        }}
      >
        {linkCopied ? 'Copied!' : 'Copy invite link'}
      </Button>
      {onSendToPatient && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<SendIcon sx={{ fontSize: 16 }} />}
          onClick={onSendToPatient}
          sx={{
            color: 'common.white',
            borderColor: 'rgba(255,255,255,0.3)',
            textTransform: 'none',
            '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
          }}
        >
          {`Send invite to ${singularCap}`}
        </Button>
      )}
    </Stack>
  );
}
